import { useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TsrctDemandStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useModuleContext } from '~/components/ModuleContextProvider';

import { DemandRecommendationForm } from './components/DemandRecommendationForm/DemandRecommendationForm';
import {
  demandRecommendationFormDataSchema,
  DemandRecommendationFormData,
  demandRecommendationFormDataDefault,
} from './components/DemandRecommendationForm/DemandRecommendationForm.schema';
import {
  formDataToMutationInput,
  lineItemsToFormData,
} from './components/DemandRecommendationForm/DemandRecommendationForm.util';
import { RecommendationFormProvider } from './components/RecommendationFormProvider/RecommendationFormProvider';
import { DEMAND_RECOMMENDATION_QUERY } from './DemandRecommendation.query.gql';
import { DEMAND_RECOMMENDATION_APPLY_MUTATION } from './DemandRecommendationApply.mutation.gql';

const disabledFormDemandStatus: Partial<Record<TsrctDemandStatus, boolean>> = {
  COMPLETED: true,
  CANCELLED: true,
};

export const DemandRecommendationWidget = () => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandRecommendationWidget' });
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const form = useForm<DemandRecommendationFormData>({
    mode: 'onTouched',
    resolver: zodResolver(demandRecommendationFormDataSchema),
    defaultValues: demandRecommendationFormDataDefault,
  });

  const { entityId } = useModuleContext();
  const { data } = useQuery(DEMAND_RECOMMENDATION_QUERY, {
    variables: { id: entityId },
  });
  const [apply, { loading: saving }] = useMutation(DEMAND_RECOMMENDATION_APPLY_MUTATION, {
    onError,
  });

  const lineItemsData = useMemo(() => data?.tesseract.demand.demandRequest.lineItems ?? [], [data]);

  const { handleSubmit, reset } = form;

  const onSubmit = async (data: DemandRecommendationFormData) => {
    const submitData = formDataToMutationInput(data);
    if (submitData.length > 0) {
      await apply({ variables: { demandId: entityId, data: submitData } });
      enqueueSuccessSnackbar(t('recommendationsApplied'));
    }
  };

  useEffect(() => {
    reset(lineItemsToFormData(lineItemsData));
  }, [lineItemsData, reset]);

  const hasWritePermission = data?.tesseract.demand.permissions.write ?? false;
  const isFormActive =
    (data && hasWritePermission && !disabledFormDemandStatus[data?.tesseract.demand.status]) ?? false;

  return (
    <RecommendationFormProvider isActive={isFormActive} {...form}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
        <DemandRecommendationForm queryData={lineItemsData} isLoading={saving} isApplyAllowed={hasWritePermission} />
      </form>
    </RecommendationFormProvider>
  );
};
